import React from "react";

import ColorPicker from "components/basic/color-picker/ColorPicker";
import { DatasetDropdown } from "components/basic/dropdowns/dataset/DatasetDropdown";
import { DropdownChangeParams } from "primereact/dropdown";
import { InputNumber, InputNumberValueChangeParams } from "primereact/inputnumber";
import { InputSwitch, InputSwitchChangeParams } from "primereact/inputswitch";
import { InputText } from "primereact/inputtext";
import { T } from "services/lang/LanguageService";
import { SparkLineProps } from "./SparkLineTypes";

export type SparkLineInputsProps = {
  currentConfig: SparkLineProps;
  onDatasetChange: (
    property: keyof SparkLineProps,
    value: string | number | boolean | null
  ) => void;
  onConfigChange: (
    e:
      | InputNumberValueChangeParams
      | React.ChangeEvent<HTMLInputElement>
      | InputSwitchChangeParams
      | DropdownChangeParams
  ) => void;
};

const SparkLineInputs: React.FC<SparkLineInputsProps> = ({
  currentConfig,
  onDatasetChange,
  onConfigChange,
}) => {
  return (
    <>
      <div className="col-12 mb-3">
        <label htmlFor="dataset">{T("word.dataset")}</label>
        <DatasetDropdown
          value={currentConfig.dataset}
          onChange={val => onDatasetChange("dataset", val)}
        />
      </div>
      <div className="w-5 md:w-2 flex flex-column mb-3">
        <label htmlFor="lineColor">{T("label.line-color")}</label>
        <ColorPicker
          id="lineColor"
          value={currentConfig.lineColor}
          onValueChange={onConfigChange}
        />
      </div>
      <div className="w-5 md:w-2 flex flex-column mb-3">
        <label htmlFor="lineWidth">{T("label.line-width")}</label>
        <InputNumber
          id="lineWidth"
          inputClassName="w-full"
          className="p-inputtext-sm"
          suffix=" px"
          value={currentConfig.lineWidth}
          onValueChange={onConfigChange}
        />
      </div>
      <div className="w-5 md:w-2 flex flex-column mb-3">
        <label htmlFor="count">{T("action.count")}</label>
        <InputNumber
          id="count"
          inputClassName="w-full"
          className="p-inputtext-sm"
          value={currentConfig.count}
          onValueChange={onConfigChange}
        />
      </div>
      <div className="w-5 md:w-3 flex flex-column mb-3">
        <label htmlFor="updateFrequency">{T("load-conf.update-frequency")}</label>
        <InputNumber
          id="updateFrequency"
          inputClassName="w-full"
          className="p-inputtext-sm"
          value={currentConfig.updateFrequency}
          onValueChange={onConfigChange}
        />
      </div>
      <div className="w-5 md:w-2 flex flex-column mb-3">
        <label htmlFor="unit">{T("word.unit")}</label>
        <InputText
          className="p-inputtext-sm"
          id="unit"
          type="text"
          value={currentConfig.unit}
          onChange={onConfigChange}
        />
      </div>
      <div className="w-5 md:w-3 flex flex-column mb-3">
        <label htmlFor="valueScaling">{T("config.spark-line.value-scaling")}</label>
        <InputNumber
          id="valueScaling"
          className="p-inputtext-sm"
          value={currentConfig.valueScaling}
          onValueChange={onConfigChange}
        />
      </div>
      <div className="w-5 md:w-3 flex flex-column mb-3">
        <label htmlFor="decimalPlaces">{T("config.decimal-places")}</label>
        <InputNumber
          id="decimalPlaces"
          className="p-inputtext-sm"
          value={currentConfig.decimalPlaces}
          onValueChange={onConfigChange}
        />
      </div>
      <div className="w-5 md:w-3 flex flex-column mb-3">
        <label htmlFor="labelFontSize">{T("config.spark-line.label-font-size")}</label>
        <InputText
          id="labelFontSize"
          type="text"
          className="p-inputtext-sm"
          value={currentConfig.labelFontSize}
          onChange={onConfigChange}
        />
      </div>
      <div className="w-5 md:w-2 flex flex-column mb-3">
        <label htmlFor="sparkLineWidth">{T("config.spark-line.spark-line-width")}</label>
        <InputNumber
          id="sparkLineWidth"
          className="p-inputtext-sm"
          value={currentConfig.sparkLineWidth}
          onValueChange={onConfigChange}
        />
      </div>
      <div className="flex gap-3 mb-5">
        <div className="flex flex-column gap-2">
          <label htmlFor="height">Height (px)</label>
          <InputNumber
            id="height"
            className="p-inputtext-sm"
            value={currentConfig.height ?? undefined}
            onValueChange={onConfigChange}
          />
        </div>
        <div className="flex flex-column justify-content-between align-items-center">
          <label htmlFor="showMinMax">MinMax</label>
          <InputSwitch
            id="showMinMax"
            className="p-inputtext-sm mb-2"
            checked={currentConfig.showMinMax}
            onChange={onConfigChange}
          />
        </div>
        <div className="flex flex-column justify-content-between align-items-center">
          <label htmlFor="showCurrent">{T("label.spark-line.show-current")}</label>
          <InputSwitch
            id="showCurrent"
            className="p-inputtext-sm mb-2"
            checked={currentConfig.showCurrent}
            onChange={onConfigChange}
          />
        </div>
      </div>
    </>
  );
};

export default SparkLineInputs;
